import {
  AIChatNumericRange,
  AIChatProgressStepKind,
  AIChatProgressStepDetails,
  AIChatProgressStepState,
  AIChatProgressTrace,
  AIChatSearchApartmentsArgs,
} from 'src/typings/ai-chat';

export interface ProgressStart {
  traceId: string;
  stepId: string;
  startedAt: number;
}

export interface ProgressAdvance {
  traceId: string;
  finishedAt: number;
  completedKind?: AIChatProgressStepKind;
  completedState?: Exclude<AIChatProgressStepState, 'active'>;
  completedDetails?: AIChatProgressStepDetails;
  nextStep: {
    id: string;
    kind: AIChatProgressStepKind;
    startedAt: number;
    detail?: string;
    details?: AIChatProgressStepDetails;
  };
}

export const createProgressTrace = ({ traceId, stepId, startedAt }: ProgressStart): AIChatProgressTrace => ({
  id: traceId,
  startedAt,
  usedTool: false,
  steps: [{
    id: stepId, kind: 'analyzing', state: 'active', startedAt,
  }],
});

export const advanceProgressTraceState = (
  trace: AIChatProgressTrace,
  payload: ProgressAdvance,
): AIChatProgressTrace => {
  if (trace.id !== payload.traceId || trace.finishedAt) return trace;
  const activeIndex = trace.steps.findIndex(({ state }) => state === 'active');
  if (activeIndex < 0) return trace;
  const finishedAt = Math.max(trace.steps[activeIndex].startedAt, payload.finishedAt);
  const steps = trace.steps.map((step, index) => {
    if (index !== activeIndex) return step;
    const completedStep = {
      ...step,
      kind: payload.completedKind || step.kind,
      state: payload.completedState || 'completed',
      finishedAt,
    };
    if (payload.completedDetails) completedStep.details = payload.completedDetails;
    else if (payload.completedKind) delete completedStep.details;
    return completedStep;
  });
  return {
    ...trace,
    usedTool: trace.usedTool || payload.nextStep.kind === 'searching',
    steps: [...steps, {
      ...payload.nextStep,
      state: 'active',
      startedAt: Math.max(finishedAt, payload.nextStep.startedAt),
    }],
  };
};

export const finishProgressTraceState = (
  trace: AIChatProgressTrace,
  state: Exclude<AIChatProgressStepState, 'active'>,
  finishedAtValue: number,
): AIChatProgressTrace => {
  if (trace.finishedAt) return trace;
  const activeIndex = trace.steps.findIndex((step) => step.state === 'active');
  if (activeIndex < 0) return trace;
  const finishedAt = Math.max(trace.steps[activeIndex].startedAt, finishedAtValue);
  return {
    ...trace,
    finishedAt,
    steps: trace.steps.map((step, index) => (index === activeIndex
      ? { ...step, state, finishedAt }
      : step)),
  };
};

export const formatProgressDuration = (milliseconds: number): string => (
  `${(Math.max(0, milliseconds) / 1000).toFixed(1)}s`
);

const LABELS: Record<AIChatProgressStepKind, string> = {
  analyzing: 'Analyzing your request',
  searching: 'Searching available placements',
  reviewing: 'Reviewing search results',
  refining: 'Refining the search',
};

export const getProgressStepLabel = (kind: AIChatProgressStepKind) => LABELS[kind];

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const validNumber = (value: unknown): value is number => Number.isFinite(value);
const value = (candidate: number) => numberFormatter.format(candidate);
const MAX_LIST_ITEM_LENGTH = 48;
const MAX_LIST_ITEMS = 5;
const MAX_SEARCH_SUMMARY_LENGTH = 240;
const MAX_PROGRESS_TEXT_LENGTH = 240;
const MAX_FINAL_RESPONSE_TEXT_LENGTH = 4000;

const truncateText = (text: string, maxLength: number): string => (
  text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text
);

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeForIdentifierDetection = (text: string): string => (
  text.replace(/[*_~`]/g, '').replace(/\s+/g, ' ')
);

const containsSensitiveIdentifier = (text: string, identifier: string): boolean => {
  const normalizedIdentifier = normalizeForIdentifierDetection(identifier.trim());
  if (!normalizedIdentifier) return false;
  const escaped = escapeRegExp(normalizedIdentifier);
  if (/^\d+$/.test(normalizedIdentifier)) {
    const numericIdentifierPrefixes = [
      '(?:(?:apartment|apt\\.?|unit|flat|home|complex|building|house|квартир\\p{L}*|комплекс\\p{L}*|жк|кв\\.?)(?:\\s+(?:id|no\\.?|number|номер))?\\s*(?:[:#-]\\s*)?)',
      '(?:(?:https?://[^\\s/]+)?/+(?:apartments?|units?|flats?|homes?|квартир\\p{L}*|кв\\.?)/+)',
      '(?:(?:https?://[^\\s/]+)?/(?:[^\\s/?#]+/)+)',
      '(?:[?&](?:[\\p{L}\\p{N}_-]*id)\\s*=\\s*)',
      'id\\s*[:#-]?\\s*',
      'no\\.?\\s*[:#-]?\\s*',
      'number\\s*[:#-]?\\s*',
      'номер\\s*[:#-]?\\s*',
      '№\\s*',
      '#\\s*',
    ];
    return new RegExp(`(?:${numericIdentifierPrefixes.join('|')})${escaped}(?!\\d)`, 'iu').test(text);
  }
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}(?=$|[^\\p{L}\\p{N}])`, 'iu').test(text);
};

const hasUnsafeAIChatText = (text: string, sensitiveIdentifiers: string[]): boolean => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (/chain[- ]of[- ]thought|hidden reasoning|internal reasoning|step-by-step analysis/i.test(normalized)) {
    return true;
  }
  const normalizedLower = normalizeForIdentifierDetection(normalized).toLocaleLowerCase();
  return sensitiveIdentifiers
    .map((identifier) => normalizeForIdentifierDetection(identifier.trim()).toLocaleLowerCase())
    .filter(Boolean)
    .some((identifier) => containsSensitiveIdentifier(normalizedLower, identifier));
};

export const sanitizeAIChatProgressText = (
  text: string | undefined,
  sensitiveIdentifiers: string[],
): string | undefined => {
  const normalized = text?.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length > MAX_PROGRESS_TEXT_LENGTH) return undefined;
  return hasUnsafeAIChatText(normalized, sensitiveIdentifiers) ? undefined : normalized;
};

/** Protect final assistant text without changing Markdown whitespace or formatting. */
export const sanitizeAIChatFinalResponseText = (
  text: string | undefined,
  sensitiveIdentifiers: string[],
): string | undefined => {
  if (!text || !text.trim() || text.length > MAX_FINAL_RESPONSE_TEXT_LENGTH) return undefined;
  return hasUnsafeAIChatText(text, sensitiveIdentifiers) ? undefined : text;
};

const safeList = (values: unknown): string[] => (
  (Array.isArray(values) ? values : [])
    .slice(0, MAX_LIST_ITEMS)
    .filter((item): item is string => typeof item === 'string')
    .map((item) => truncateText(item.trim(), MAX_LIST_ITEM_LENGTH))
    .filter(Boolean)
);

const isRecord = (candidate: unknown): candidate is Record<string, unknown> => (
  typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate)
);

const isNumericRange = (candidate: unknown): candidate is AIChatNumericRange => (
  isRecord(candidate)
  && (candidate.min === undefined || validNumber(candidate.min))
  && (candidate.max === undefined || validNumber(candidate.max))
);

const countRange = (
  range: unknown,
  singular: string,
  plural: string,
): string | undefined => {
  if (!isNumericRange(range)) return undefined;
  const { min, max } = range;
  if (validNumber(min) && validNumber(max)) {
    if (min === max) return `${value(min)} ${min === 1 ? singular : plural}`;
    return `${value(min)}–${value(max)} ${plural}`;
  }
  if (validNumber(min)) return `${value(min)}+ ${plural}`;
  if (validNumber(max)) return `up to ${value(max)} ${plural}`;
  return undefined;
};

const measurementRange = (
  range: unknown,
  unit: string,
): string | undefined => {
  if (!isNumericRange(range)) return undefined;
  const { min, max } = range;
  if (validNumber(min) && validNumber(max)) {
    return min === max
      ? `${value(min)} ${unit}`
      : `${value(min)}–${value(max)} ${unit}`;
  }
  if (validNumber(min)) return `from ${value(min)} ${unit}`;
  if (validNumber(max)) return `up to ${value(max)} ${unit}`;
  return undefined;
};

const floorRange = (range: unknown): string | undefined => {
  if (!isNumericRange(range)) return undefined;
  const { min, max } = range;
  if (validNumber(min) && validNumber(max)) {
    return min === max ? `floor ${value(min)}` : `floors ${value(min)}–${value(max)}`;
  }
  if (validNumber(min)) return `floor ${value(min)}+`;
  if (validNumber(max)) return `up to floor ${value(max)}`;
  return undefined;
};

const textList = (
  values: unknown,
  singular: string,
  plural: string,
): string | undefined => {
  const safe = safeList(values);
  if (safe.length === 0) return undefined;
  return `${safe.length === 1 ? singular : plural} ${safe.join(', ')}`;
};

const compactSegments = (segments: Array<string | undefined>): string | undefined => {
  const safe = segments.filter((segment): segment is string => Boolean(segment));
  if (safe.length === 0) return undefined;
  if (safe.length <= 4) return safe.join(' · ');
  return [...safe.slice(0, 3), `+${safe.length - 3} criteria`].join(' · ');
};

export const formatApartmentSearchSummary = (
  args: AIChatSearchApartmentsArgs,
): string | undefined => {
  if (!isRecord(args)) return undefined;
  const price = measurementRange(args.totalPrice, 'total price');
  const excludedFloors = (Array.isArray(args.excludedFloors) ? args.excludedFloors : []).filter(validNumber);
  const sort = isRecord(args.sort) && ['totalPrice', 'totalArea', 'floor'].includes(String(args.sort.field))
    ? `${args.sort.field === 'totalPrice' ? 'price' : args.sort.field === 'totalArea' ? 'area' : 'floor'} ${args.sort.direction === 'asc' ? 'low to high' : 'high to low'}`
    : undefined;
  const summary = compactSegments([
    countRange(args.rooms, 'room', 'rooms'),
    countRange(args.bedrooms, 'bedroom', 'bedrooms'),
    countRange(args.bathrooms, 'bathroom', 'bathrooms'),
    measurementRange(args.totalArea, 'm²'),
    price,
    floorRange(args.floor),
    excludedFloors.length ? `excluding floors ${excludedFloors.map(value).join(', ')}` : undefined,
    textList(args.houses, 'house', 'houses'),
    textList(args.sections, 'section', 'sections'),
    textList(args.planningTypes, 'planning type', 'planning types'),
    safeList(args.features).join(', ') || undefined,
    sort,
    validNumber(args.limit) ? `up to ${value(args.limit)} results` : undefined,
  ]);
  return summary ? truncateText(summary, MAX_SEARCH_SUMMARY_LENGTH) : undefined;
};
